import { useCallback, useEffect, useRef } from 'react'
import { useAtomValue } from 'jotai'
import posthog from 'posthog-js'
import { currentSpaceAtom, currentUserDataAtom } from '@/store/auth.atom'
import { currentTierAtom } from '@/store/pricing.atom'
import { trialStatusAtom } from '@/store/trial.atom'

// ============================================
// Configuration
// ============================================

const POSTHOG_API_KEY = import.meta.env.VITE_POSTHOG_API_KEY as
  | string
  | undefined
const POSTHOG_HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string) || 'https://app.posthog.com'

// ============================================
// Event Types
// ============================================

export type PricingEvent =
  | 'pricing_page.viewed'
  | 'pricing_page.plan_selected'
  | 'checkout.started'
  | 'checkout.completed'
  | 'checkout.abandoned'
  | 'upgrade_prompt.shown'
  | 'upgrade_prompt.clicked'
  | 'billing_portal.opened'
  | 'plan.upgraded'
  | 'plan.downgraded'
  | 'subscription.canceled'
  | 'limit.reached'
  | 'expansion.added'

export type ActivationEvent =
  | 'trial.signup_completed'
  | 'trial.first_resource_created'
  | 'trial.first_booking_made'
  | 'trial.team_invited'
  | 'trial.calendar_connected'
  | 'trial.floor_plan_uploaded'

export type TrialBannerEvent = 'trial_banner.clicked' | 'trial_banner.dismissed'

export type AnalyticsEvent =
  | PricingEvent
  | ActivationEvent
  | TrialBannerEvent
  | string

export interface AnalyticsEventProperties {
  [key: string]:
    | string
    | number
    | boolean
    | null
    | undefined
    | (string | number | boolean | null | undefined)[]
}

// ============================================
// Initialization
// ============================================

let isInitialized = false

function initializePostHog() {
  if (isInitialized || !POSTHOG_API_KEY) {
    return
  }

  try {
    posthog.init(POSTHOG_API_KEY, {
      api_host: POSTHOG_HOST,
      // Disable automatic page view tracking - we'll track manually
      capture_pageview: false,
      // Disable automatic pageleave tracking
      capture_pageleave: false,
      // Respect Do Not Track
      respect_dnt: true,
      // Persistence
      persistence: 'localStorage',
      // Session recording disabled by default
      disable_session_recording: true,
    })
    isInitialized = true
    console.log('[Analytics] PostHog initialized')
  } catch (error) {
    console.error('[Analytics] Failed to initialize PostHog:', error)
  }
}

// ============================================
// Hook
// ============================================

/**
 * Analytics hook for tracking user events via PostHog
 *
 * @example
 * const { track, identify } = useAnalytics()
 *
 * // Track an event
 * track('upgrade_prompt.clicked', { trigger: 'trial_banner' })
 *
 * // Identify a user
 * identify()
 */
export function useAnalytics() {
  const space = useAtomValue(currentSpaceAtom)
  const user = useAtomValue(currentUserDataAtom)
  const pricingTier = useAtomValue(currentTierAtom)
  const trialStatus = useAtomValue(trialStatusAtom)
  const identifiedRef = useRef(false)

  // Initialize PostHog on first mount
  useEffect(() => {
    initializePostHog()
  }, [])

  // Check if analytics is enabled
  const isEnabled = (): boolean => {
    return isInitialized && !!POSTHOG_API_KEY
  }

  /**
   * Track an analytics event
   */
  const track = useCallback(
    (event: AnalyticsEvent, properties?: AnalyticsEventProperties) => {
      if (!isEnabled()) {
        return
      }

      try {
        posthog.capture(event, {
          ...properties,
          // Add context
          pricing_tier: pricingTier,
          trial_status: trialStatus,
          space_id: space?.id,
          space_name: space?.name,
          user_id: user?.id,
          source: 'web',
        })
      } catch (error) {
        console.error('[Analytics] Failed to track event:', error)
      }
    },
    [pricingTier, trialStatus, space, user]
  )

  /**
   * Identify the current user
   */
  const identify = useCallback(() => {
    if (!isEnabled() || !user || identifiedRef.current) {
      return
    }

    try {
      // Identify user
      posthog.identify(`user_${user.id}`, {
        email: user.email,
        name: user.name,
      })

      // Identify space as a group
      if (space) {
        posthog.group('space', `space_${space.id}`, {
          name: space.name,
          pricing_tier: pricingTier,
          trial_status: trialStatus,
        })
      }

      identifiedRef.current = true
    } catch (error) {
      console.error('[Analytics] Failed to identify user:', error)
    }
  }, [user, space, pricingTier, trialStatus])

  /**
   * Reset analytics (call on logout)
   */
  const reset = useCallback(() => {
    if (!isEnabled()) {
      return
    }

    try {
      posthog.reset()
      identifiedRef.current = false
    } catch (error) {
      console.error('[Analytics] Failed to reset:', error)
    }
  }, [])

  /**
   * Track a page view
   */
  const pageView = useCallback(
    (path?: string) => {
      if (!isEnabled()) {
        return
      }

      try {
        posthog.capture('$pageview', {
          $current_url: path || window.location.pathname,
          pricing_tier: pricingTier,
          trial_status: trialStatus,
          space_id: space?.id,
        })
      } catch (error) {
        console.error('[Analytics] Failed to track page view:', error)
      }
    },
    [pricingTier, trialStatus, space]
  )

  return {
    track,
    identify,
    reset,
    pageView,
    isEnabled,
  }
}

export default useAnalytics
